<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);

const { t } = useI18n();
const dialogRef = ref(null);

const STAGES = [
  'prospecting',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'lost',
];

const stageOptions = STAGES.map(stage => ({
  value: stage,
  label: t(`OPPORTUNITIES.STAGES.${stage.toUpperCase()}`),
}));

const emptyForm = () => ({
  name: '',
  value: '',
  currency: '',
  stage: 'prospecting',
  manualProbability: '',
  expectedCloseDate: '',
});

const form = reactive(emptyForm());

const isFormInvalid = computed(() => !form.name.trim());

const resetForm = () => {
  Object.assign(form, emptyForm());
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;

  emit('create', {
    name: form.name.trim(),
    value: form.value || null,
    currency: form.currency.trim() || null,
    stage: form.stage,
    manualProbability: form.manualProbability || null,
    expectedCloseDate: form.expectedCloseDate || null,
  });
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const onSuccess = () => {
  resetForm();
  closeDialog();
};

defineExpose({ dialogRef, onSuccess, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-6">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('OPPORTUNITIES.CREATE.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          v-model="form.name"
          :placeholder="t('OPPORTUNITIES.FIELDS.NAME')"
          :disabled="isLoading"
          autofocus
        />
        <Select
          v-model="form.stage"
          :options="stageOptions"
          :placeholder="t('OPPORTUNITIES.FIELDS.STAGE')"
          :disabled="isLoading"
        />
        <Input
          v-model="form.value"
          type="number"
          :placeholder="t('OPPORTUNITIES.FIELDS.VALUE')"
          :disabled="isLoading"
        />
        <Input
          v-model="form.currency"
          :placeholder="t('OPPORTUNITIES.FIELDS.CURRENCY')"
          :disabled="isLoading"
        />
        <Input
          v-model="form.manualProbability"
          type="number"
          :placeholder="t('OPPORTUNITIES.FIELDS.MANUAL_PROBABILITY')"
          :disabled="isLoading"
        />
        <input
          v-model="form.expectedCloseDate"
          type="date"
          :disabled="isLoading"
          class="w-full h-8 px-3 text-sm bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          :label="t('OPPORTUNITIES.CREATE.ACTIONS.SAVE')"
          color="blue"
          type="submit"
          :disabled="isFormInvalid || isLoading"
          :is-loading="isLoading"
        />
      </div>
    </template>
  </Dialog>
</template>
